import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import { selectUser, selectIsAuthenticated } from "../../store/slices/authSlice";
import { canAccessRoute } from "../../utils/permissions";
import AccessDenied from "../AccessDenied/AccessDenied";
import Loader from "../Loader";
import DefaultLayout from "../../layout/DefaultLayout";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: {
    resource: string;
    action: string;
  };
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredPermission }) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectUser);
  const location = useLocation();

  // Show loader while authentication state is being determined
  if (isAuthenticated === null) {
    return <Loader />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user account is disabled
  if (user.isDisabled) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Account Disabled</h2>
          <p className="text-gray-600 mb-6">
            Your account has been disabled. Please contact the system administrator.
          </p>
          <button
            onClick={() => {
              // Implement logout functionality
              window.location.href = "/login";
            }}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // Check route-specific permissions
  const currentPath = location.pathname;
  const hasRouteAccess = canAccessRoute(user, currentPath);

  if (!hasRouteAccess) {
    return (
      <DefaultLayout>
        <AccessDenied />
      </DefaultLayout>
    );
  }

  // Check specific permission if provided
  if (requiredPermission) {
    const hasSpecificPermission =
      user.role === "SUPER_ADMIN" ||
      user.permissions.some(
        (permission) =>
          permission.resource === requiredPermission.resource &&
          permission.action === requiredPermission.action
      );

    if (!hasSpecificPermission) {
      return (
      <DefaultLayout>
        <AccessDenied />
      </DefaultLayout>
    );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
